import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { adminApi, formatApiError } from "@/lib/adminApi";
import { StatusLabel, money, when } from "@/studio/ui";
import { SummaryView } from "@/studio/intake/ReviewStep";

const CLEARS = {
  stage1: "Stage 1 answers: room goals and needs, design timeline and delivery window, styles and palettes (including room-specific styles), priorities, deal breakers and notes.",
  stage2: "Stage 2 answers: measurements, door and window sizes, photos and floor plans, the furniture checklist, requirements and optional-service interest.",
};
CLEARS.combined = `${CLEARS.stage1} Also ${CLEARS.stage2.charAt(0).toLowerCase()}${CLEARS.stage2.slice(1)}`;
const SHORT = { stage1: "Stage 1", stage2: "Stage 2", combined: "Combined" };

export const StageTag = ({ st, testId }) => {
  if (st.status === "completed") return <span className="st-tag st-tag--ok" data-testid={testId}>Completed {when(st.completed_at)}{st.last_edited_at ? ` · edited ${when(st.last_edited_at)}` : ""}</span>;
  if (st.status === "in_progress") return <span className="st-tag st-tag--warn" data-testid={testId}>In progress</span>;
  return <span className="st-tag st-tag--unk" data-testid={testId}>Not started</span>;
};

export const useStartStage = () => {
  const navigate = useNavigate();
  return async (projectId, stage) => {
    try {
      const { data } = await adminApi.post(`/studio/projects/${projectId}/consultations`, { type: stage });
      navigate(`/admin/studio/consultations/${data.id}?stage=${stage}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };
};

const DeleteDialog = ({ st, project, onClose, onProject }) => {
  const stages = project.workflow.stages;
  const blocked = st?.stage === "stage1" && stages.find((s) => s.stage === "stage2")?.status !== "not_started" ? "Stage 2 depends on Stage 1. Delete Stage 2 first." : project.has_signed_agreement ? "This project has a signed agreement, so its consultation can't be deleted." : null;
  const confirm = async () => {
    try {
      const { data } = await adminApi.delete(`/studio/consultations/${project.workflow.intake_id}`, { params: { stage: st.stage } });
      onProject(data);
      toast.success(`${SHORT[st.stage]} deleted. You can restore it from the history.`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
    onClose();
  };
  return (
    <AlertDialog open={!!st} onOpenChange={(o) => !o && onClose()}>
      <AlertDialogContent className="st-dialog" data-testid="delete-stage-dialog">
        <AlertDialogTitle>Delete {st?.label}?</AlertDialogTitle>
        <AlertDialogDescription asChild>
          <div style={{ display: "grid", gap: 10, color: "#6b6a65", fontSize: 13.5 }}>
            {blocked ? <p className="st-note st-note--warn" data-testid="delete-stage-blocked">{blocked}</p> : (
              <>
                <p><strong style={{ color: "#2e2e2c" }}>This clears</strong> {st && CLEARS[st.stage]}</p>
                <p><strong style={{ color: "#2e2e2c" }}>Kept:</strong> the client, the project, its rooms, room furniture budgets and design pricing.</p>
                <p>It's a soft delete: staff can restore it from the consultation history.</p>
              </>
            )}
          </div>
        </AlertDialogDescription>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="delete-stage-cancel">Cancel</AlertDialogCancel>
          {!blocked && <AlertDialogAction onClick={confirm} style={{ background: "#8f4a3d" }} data-testid="delete-stage-confirm">Delete</AlertDialogAction>}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

const TypeDialog = ({ open, project, onClose, onProject }) => {
  const to = project.consultation_type === "remote" ? "local" : "remote";
  const confirm = async () => {
    try {
      const { data } = await adminApi.patch(`/studio/projects/${project.id}/consultation-type`, { consultation_type: to, acknowledge_agreement: project.has_signed_agreement });
      onProject(data);
      toast.success(`Project type changed to ${to === "local" ? "Local" : "Remote"}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
    onClose();
  };
  return (
    <AlertDialog open={open} onOpenChange={(o) => !o && onClose()}>
      <AlertDialogContent className="st-dialog" data-testid="change-type-dialog">
        <AlertDialogTitle>Change to {to === "local" ? "Local" : "Remote"}?</AlertDialogTitle>
        <AlertDialogDescription asChild>
          <div style={{ display: "grid", gap: 10, color: "#6b6a65", fontSize: 13.5 }}>
            <p>{to === "remote" ? "Stage 1 and Stage 2 become one Combined consultation. If Stage 1 is complete, Combined continues at screen 5." : "The Combined consultation splits into Stage 1 (discovery) and Stage 2 (in-home assessment), each keeping its progress."}</p>
            <p>All answers, measurements, photos, furniture, budgets and styles are kept. Nothing is copied or overwritten.</p>
            {project.has_signed_agreement && <p className="st-note st-note--warn">A signed agreement exists. Its agreed scope and terms do not change; an amendment is needed.</p>}
          </div>
        </AlertDialogDescription>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="change-type-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={confirm} data-testid="change-type-confirm">Change type</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export const ConsultationSlots = ({ project, onProject }) => {
  const navigate = useNavigate();
  const start = useStartStage();
  const [del, setDel] = useState(null);
  const [typeOpen, setTypeOpen] = useState(false);
  const wf = project.workflow;
  const open = (st, mode) => navigate(`/admin/studio/consultations/${wf.intake_id}?stage=${st.stage}${mode ? `&mode=${mode}` : ""}`);
  const restore = async (d) => {
    try { const { data } = await adminApi.post(`/studio/consultations/${wf.intake_id}/restore`, { deletion_id: d.id }); onProject(data); toast.success("Restored"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const deleted = wf.deletions.filter((d) => !d.restored_at);
  return (
    <section className="st-panel" data-testid="project-consultations">
      <div className="st-panel__head">
        <h2 className="st-h2">Consultation</h2>
        <span style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <span className="st-tag st-tag--sage" data-testid="project-type-label">{project.consultation_type === "remote" ? "Remote" : "Local"}</span>
          <button className="st-btn st-btn--quiet st-btn--sm" onClick={() => setTypeOpen(true)} data-testid="change-type-button">Change type</button>
        </span>
      </div>
      <ul className="st-list">
        {wf.stages.map((st) => (
          <li key={st.stage} className="st-row" style={{ flexWrap: "wrap" }} data-testid={`stage-${st.stage}`}>
            <div><div className="st-row__title">{st.label}</div><div style={{ marginTop: 6 }}><StageTag st={st} testId={`stage-${st.stage}-status`} /></div>
              {st.locked && <div className="st-muted st-small" style={{ marginTop: 6 }}>{st.locked}</div>}</div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {st.status === "not_started" && <button className="st-btn st-btn--sm" disabled={!!st.locked} onClick={() => start(project.id, st.stage)} data-testid={`stage-${st.stage}-start`}>Start</button>}
              {st.status === "in_progress" && <button className="st-btn st-btn--sm" onClick={() => open(st)} data-testid={`stage-${st.stage}-resume`}>Resume</button>}
              {st.status === "completed" && <>
                <button className="st-btn st-btn--ghost st-btn--sm" onClick={() => open(st, "view")} data-testid={`stage-${st.stage}-view`}>View</button>
                <button className="st-btn st-btn--ghost st-btn--sm" onClick={() => open(st, "edit")} data-testid={`stage-${st.stage}-edit`}>Edit</button>
                <button className="st-btn st-btn--quiet st-btn--sm st-btn--danger" onClick={() => setDel(st)} data-testid={`stage-${st.stage}-delete`}>Delete</button>
              </>}
              {st.status === "in_progress" && <button className="st-btn st-btn--quiet st-btn--sm st-btn--danger" onClick={() => setDel(st)} data-testid={`stage-${st.stage}-delete`}>Delete</button>}
            </div>
          </li>
        ))}
      </ul>
      {deleted.length > 0 && (
        <div style={{ marginTop: 14 }} data-testid="deleted-consultations">
          <p className="st-label">Deleted</p>
          {deleted.map((d) => (
            <div className="st-row" key={d.id}><span className="st-small st-muted">{d.summary} · {when(d.deleted_at)}</span>
              {d.restorable ? <button className="st-btn st-btn--ghost st-btn--sm" onClick={() => restore(d)} data-testid={`restore-${d.id}`}>Restore</button>
                : <span className="st-tag st-tag--unk" data-testid={`superseded-${d.id}`}>Superseded, not restorable</span>}</div>
          ))}
        </div>
      )}
      <DeleteDialog st={del} project={project} onClose={() => setDel(null)} onProject={onProject} />
      <TypeDialog open={typeOpen} project={project} onClose={() => setTypeOpen(false)} onProject={onProject} />
    </section>
  );
};

export const ConsultationHistory = ({ projects }) => (
  projects.length === 0 ? <p className="st-muted st-small">No consultations yet.</p> : (
    <ul className="st-list" data-testid="consultation-history">
      {projects.flatMap((p) => p.stages.map((st) => (
        <li key={`${p.id}-${st.stage}`}>
          <Link to={`/admin/studio/projects/${p.id}`} className="st-row st-row--link" data-testid={`history-${p.id}-${st.stage}`}>
            <div><div className="st-row__title">{st.label}</div><div className="st-row__meta">{p.name}</div></div>
            <StageTag st={st} />
          </Link>
        </li>
      )))}
    </ul>
  )
);

export const StatusPanel = ({ project }) => {
  const start = useStartStage();
  const na = project.workflow.next_action;
  return (
    <section className="st-panel" data-testid="project-status-panel">
      <div className="st-panel__head"><h2 className="st-h2">Status</h2><StatusLabel status={project.status} testId="project-status-label" /></div>
      <div className="st-row" style={{ paddingTop: 0 }}>
        <span><span className="st-label">Next action</span><br /><strong data-testid="project-next-action">{na.label}</strong></span>
        {na.kind === "consultation" && <button className="st-btn st-btn--sm" onClick={() => start(project.id, na.stage)} data-testid="project-next-action-button">{na.label}</button>}
        {["agreement", "share"].includes(na.kind) && <button className="st-btn st-btn--ghost st-btn--sm" onClick={() => document.getElementById(na.kind === "agreement" ? "agreement" : "share")?.scrollIntoView({ behavior: "smooth" })} data-testid="project-next-action-button">{na.kind === "agreement" ? "Open agreement" : "Open sharing"}</button>}
      </div>
      <p className="st-muted st-small">Status is automatic: it follows the consultation, deposit and agreement records.</p>
      {project.status === "Ready for Design" && <p className="st-note" data-testid="ready-for-design">Discovery and assessment complete, deposit paid, agreement signed, fees set, nothing pending.</p>}
    </section>
  );
};

export const BeforeAgreement = ({ project }) => {
  const c = project.agreement_checklist || { measurements: [], pricing: [] };
  return (
    <section className="st-panel" data-testid="before-agreement">
      <div className="st-panel__head"><h2 className="st-h2">Before agreement</h2>
        <span className={`st-tag ${project.ready_for_agreement ? "st-tag--sage" : "st-tag--warn"}`} data-testid="ready-for-agreement">{project.ready_for_agreement ? "Ready" : "Not ready"}</span></div>
      {project.ready_for_agreement ? <p className="st-muted st-small">All measurements are confirmed and every fee is set.</p> : (
        <ul className="st-list st-check">
          {c.measurements.map((l) => <li key={`m-${l}`}><span>{l}</span><span className="st-tag st-tag--unk">Final measurements</span></li>)}
          {c.pricing.map((l) => <li key={`p-${l}`}><span>{l}</span><span className="st-tag st-tag--warn">Fee needed</span></li>)}
          {project.rooms.length === 0 && <li><span>Add rooms</span></li>}
        </ul>
      )}
    </section>
  );
};

export const budgetLines = (r) => {
  const fb = r.furniture_budget || {};
  const rec = fb.rec_min != null ? `${money(fb.rec_min)}–${money(fb.rec_max)}` : null;
  const custom = fb.custom_min != null ? (fb.custom_max && fb.custom_max !== fb.custom_min ? `${money(fb.custom_min)}–${money(fb.custom_max)}` : money(fb.custom_min)) : null;
  const selected = fb.mode === "custom" ? custom : rec;
  return { rec, selected, mode: fb.mode };
};

export const BudgetTable = ({ rooms, total, testId = "furniture-budget-total" }) => (
  <ul className="st-list st-price">
    {rooms.map((r) => {
      const b = budgetLines(r);
      return (
        <li key={r.id}><span>{r.label}<br /><span className="st-muted st-small">Recommended {b.rec || "— none for this type"}</span></span>
          <span style={{ textAlign: "right" }}>{b.selected ? <>{b.selected}<br /><span className="st-muted st-small">{b.mode === "custom" ? "Custom target" : "Recommended"}</span></> : <span className="st-tag st-tag--warn">Amount needed</span>}</span></li>
      );
    })}
    <li className="st-price__sum"><span style={{ alignSelf: "center" }}>Combined</span><span className="st-price__total" data-testid={testId}>{total.min === total.max ? money(total.min) : `${money(total.min)}–${money(total.max)}`}</span></li>
  </ul>
);

export const FurnitureBudget = ({ project }) => (
  <section className="st-panel st-panel--budget" data-testid="furniture-budget-panel">
    <div className="st-panel__head"><h2 className="st-h2">Furniture budget</h2><span className="st-tag">Separate from design fees</span></div>
    <BudgetTable rooms={project.rooms} total={project.furniture_total || { min: 0, max: 0 }} />
    <p className="st-note" data-testid="furniture-budget-note">Furniture budgets exclude design fees, delivery, and assembly.</p>
  </section>
);

export const ConsultationSummary = ({ project }) => {
  const wf = project.workflow;
  const started = wf.stages.some((s) => s.status !== "not_started");
  const [b, setB] = useState(null);
  useEffect(() => { if (wf.intake_id && started) adminApi.get(`/studio/consultations/${wf.intake_id}`).then((r) => setB(r.data)); }, [wf.intake_id, started, project.updated_at]);
  if (!started || !b) return null;
  return (
    <section className="st-panel" data-testid="consultation-summary">
      <h2 className="st-h2">Consultation summary</h2>
      <SummaryView b={b} sections={["discovery", "assessment"]} />
    </section>
  );
};

export const ProjectActivity = ({ projectId, version }) => {
  const [items, setItems] = useState([]);
  useEffect(() => {
    adminApi.get("/studio/activity", { params: { project_id: projectId, limit: 20 } }).then((r) => setItems(r.data));
  }, [projectId, version]);
  return (
    <section className="st-panel" data-testid="project-activity">
      <h2 className="st-h2">Activity</h2>
      {items.length === 0 ? <p className="st-muted">No activity yet.</p> : (
        <ul className="st-list st-activity">
          {items.map((a) => (
            <li key={a.id}><span><strong style={{ fontWeight: 600 }}>{a.action}</strong>{a.detail && <span className="st-muted"> — {a.detail}</span>}</span><time>{when(a.timestamp)}</time></li>
          ))}
        </ul>
      )}
    </section>
  );
};
